import moment from "moment-timezone";

import {
  GuestInviteResponse,
  ScreenInviteResponse,
  ScreenChildType,
  ConnectUserResponse,
  TenantResponse,
} from "../api/ApiClient";
import { apiFetchParams } from "./api-fetch-params";
import { ApiClient } from "../api/ApiClient";
import auth from "shared/Auth0";
import { authFetch } from "shared/api/authFetch";

export const apiClientFactory = () => {
  return new ApiClient(
    { getAccessToken: auth.getAccessToken },
    apiFetchParams.baseApiUrl,
    { fetch: authFetch },
  );
};
export interface LockIdentifier {
  propertyName: string;
  unitName: string;
}

export function confirmPrompt({
  promptMessage,
  onConfirm,
  onCancel,
}: {
  promptMessage: string;
  onConfirm: () => any;
  onCancel?: () => any;
}) {
  const confirmed = window.confirm(promptMessage);
  if (confirmed) {
    onConfirm && onConfirm();
  } else {
    onCancel && onCancel();
  }
}

async function throwResponseErrorIfAny(res: Response) {
  if (res.status >= 300) {
    let json = null;
    try {
      json = await res.clone().json();
    } catch (e) {
      console.error("Error parsing JSON response", e);
    }

    if (json) {
      throw new Error(json.message ? json.message : json);
    } else {
      throw new Error(res.statusText || "status " + res.status);
    }
  }
}

interface ApiError {
  statusText: string;
  status: string;
  message: string;
}

export function apiErrorStr(e: ApiError | Error) {
  return e.message && typeof e.message === "string"
    ? e.message
    : JSON.stringify(e);
}

export async function myFetch(url: string, init?: any): Promise<Response> {
  const Authorization = auth.getAccessToken();
  const options = {
    ...init,
    headers: { ...(init ? init.headers : {}), Authorization },
  };

  const res = await fetch(url, { ...options });
  await throwResponseErrorIfAny(res);
  return res;
}

export interface PageInfo {
  page: number;
  pageSize: number;
}

export interface FetchParams extends PageInfo {
  searchTerm: string;
}

export const defaultPageSize = 30;

export function reduceSetFetchParams<State>(
  isLoading: boolean,
  { page, pageSize, searchTerm }: FetchParams,
  state: State,
) {
  return Object.assign({}, state, {
    page: searchTerm !== (state as any).searchTerm ? 0 : page, //reset page to 0 on changing search term
    pageSize,
    searchTerm,
  });
}

export function reduceNextPage<State extends PageInfo>(
  list: Array<any>,
  isLoading: boolean,
  state: State,
) {
  if (list.length >= state.pageSize) {
    return Object.assign({}, state, {
      page: state.page + 1,
    });
  }
  return state;
}

export function reducePrevPage<State extends PageInfo>(
  isLoading: boolean,
  state: State,
) {
  if (state.page > 0) {
    return Object.assign({}, state, {
      page: state.page - 1,
    });
  }
  return state;
}

function getCurrentTenant(
  user: ConnectUserResponse | undefined,
): TenantResponse | undefined {
  if (user !== undefined && user.tenants !== undefined) {
    const tenant = user.tenants.find((e) => e.tenantId === user.activeTenantId);
    if (tenant !== undefined && tenant.tenant !== undefined) {
      return tenant.tenant;
    }
  }
  return undefined;
}

export function getExternalTenantId(
  user: ConnectUserResponse | undefined,
): string {
  const tenant = getCurrentTenant(user);
  if (tenant !== undefined && tenant.externalId !== undefined) {
    return tenant.externalId;
  }
  return "";
}

export function getCurrentTenantName(
  user: ConnectUserResponse | undefined,
): string {
  const tenant = getCurrentTenant(user);
  if (tenant !== undefined && tenant.name !== undefined) {
    return tenant.name;
  }
  return "";
}
export function isAdmin(user: ConnectUserResponse | undefined): boolean {
  if (user !== undefined && user.tenants !== undefined) {
    const tenant = user.tenants.find((e) => e.tenantId === user.activeTenantId);
    if (tenant !== undefined) {
      return tenant.role === "admin";
    }
  }
  return false;
}

export function localTimeToDate(date: string) {
  return moment(date).toDate();
}

export function formatDateAsLocalTime(date: Date) {
  if (!date) return "";
  return moment(date).format("YYYY-MM-DDTHH:mm");
}

export function formatLocalTime(
  date: string,
  timeFormat = "YYYY, MMM Do, h:mm a",
) {
  if (!date) return "";
  // string is in "2019-03-13T18:00:00.fffZ" format - represents GMT
  return moment(date).format(timeFormat);
}

export function toUTCTime(date: string) {
  if (!date) return "";
  // string is in "2019-03-13T18:00" format, represents local time
  return moment(date).utc().format("YYYY-MM-DDTHH:mm");
}

// Null/undefined valid-from/valid-to timestamps are a valid server state (e.g. no expiration), not an error
export function formatDateOrDefault(
  date: string | Date | null | undefined,
  timeFormat = "YYYY-MM-DD h:mm A",
  emptyValue = "--",
) {
  if (!date) return emptyValue;
  return moment(date).format(timeFormat);
}

export function toUTCDate(date: string) {
  if (!date) return undefined;
  // string is in "2019-03-13T18:00" format, represents local time
  return moment(date).toDate();
}

export function addMinutes(date: Date, minutes: number) {
  return new Date(date.getTime() + minutes * 60 * 1000);
}

export function formatLockInfos(lockIdentifiers: string) {
  const infos = lockIdentifiers
    ? lockIdentifiers.split(",").map((l) => l.trim())
    : [];
  return infos
    .map((info: string) => {
      const toks = info ? info.split(":") : [];

      if (toks.length < 1) return null;

      if (toks.length === 2) {
        return {
          propertyName: toks[0],
          unitName: toks[1],
        };
      }
      return {
        propertyName: "",
        unitName: toks[0],
      } as LockIdentifier;
    })
    .filter((info) => !!info) as LockIdentifier[];
}

export type InviteStatus = "Scheduled" | "Active" | "Expired";
export type InviteClassName = "warning" | "success" | "danger" | null;

function backendStrToMoment(date: string) {
  // string is in "2019-03-13T18:00:00.fffZ" format - represents GMT
  return moment(date);
}

function backendStrToDate(date: string) {
  return backendStrToMoment(date).toDate();
}

export function inviteStatus(
  now: Date,
  { validFrom, validTo }: GuestInviteResponse | ScreenInviteResponse,
): InviteStatus {
  const nowDt = now.getTime();
  if (nowDt < backendStrToDate(validFrom as any).getTime()) {
    return "Scheduled";
  }
  if (nowDt > backendStrToDate(validTo as any).getTime()) {
    return "Expired";
  }
  return "Active";
}

function inviteStatusToClassName(status: InviteStatus): InviteClassName {
  switch (status) {
    case "Scheduled":
      return "warning";
    case "Active":
      return "success";
    case "Expired":
      return "danger";
    default:
      return null;
  }
}

export function inviteClassName(
  now: Date,
  keyRequest: GuestInviteResponse | ScreenInviteResponse,
) {
  return inviteStatusToClassName(inviteStatus(now, keyRequest));
}

export function guestInviteWithSortedLocks(invite: GuestInviteResponse) {
  const haveSortKey = (invite.lockIds || []).every(
    (l) => l.sortKey !== undefined,
  );
  if (!haveSortKey) return invite;
  const lockIds = [...(invite.lockIds || [])];
  lockIds.sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));
  return { ...invite, lockIds };
}

export function screenInviteWithSortedScreens(invite: ScreenInviteResponse) {
  const haveSortKey = (invite.screenTiles || []).every(
    (l) => l.sortKey !== undefined,
  );
  if (!haveSortKey) return invite;
  const screenTiles = [...(invite.screenTiles || [])];
  screenTiles.sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));
  return { ...invite, screenTiles };
}

export function screenDeployButtonLabel(childType: ScreenChildType) {
  switch (childType) {
    case ScreenChildType.Proposal:
      return "Accept Proposal";
    case ScreenChildType.Version:
      return "Deploy";
    case ScreenChildType.Scheduled:
      return "Activate";
    default:
      return "";
  }
}

export const preferredSiteTimeZones = [
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Detroit",
  "America/Indiana/Indianapolis",
  "America/Vancouver",
  "America/Boise",
  "America/Toronto",
];

export const defaultSiteTimeZone = "America/Chicago";

export const northAmericaCountryCodes = ["US", "CA", "MX"];

const northAmericaIanaTimeZones = new Set<string>(
  northAmericaCountryCodes.flatMap(
    (countryCode) => moment.tz.zonesForCountry(countryCode) || [],
  ),
);

export const northAmericaTimeZonePrefixes = [
  "US/",
  "Canada/",
  "Mexico/",
];

export type TimeZoneOption = {
  value: string;
  isPreferred: boolean;
  isInUse: boolean;
  isCustomAction?: boolean;
};

export function isNorthAmericaTimeZoneForDisplay(timeZone: string) {
  const normalizedTimeZone = timeZone?.trim();

  if (!normalizedTimeZone) {
    return false;
  }

  if (northAmericaIanaTimeZones.has(normalizedTimeZone)) {
    return true;
  }

  return northAmericaTimeZonePrefixes.some((prefix) =>
    normalizedTimeZone.startsWith(prefix),
  );
}

export function getComprehensiveTimeZones(tenantUsedTimeZones: string[] = []) {
  let ianaTimeZones: string[] = [];

  const intlWithSupportedValues = Intl as unknown as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };

  if (typeof intlWithSupportedValues.supportedValuesOf === "function") {
    try {
      ianaTimeZones = intlWithSupportedValues.supportedValuesOf("timeZone");
    } catch (_error) {
      ianaTimeZones = [];
    }
  }

  if (!ianaTimeZones.length) {
    ianaTimeZones = moment.tz.names();
  }

  const orderedTimeZones = [
    ...(tenantUsedTimeZones || []).filter(Boolean),
    ...preferredSiteTimeZones,
    ...ianaTimeZones,
  ];

  const seen = new Set<string>();
  const deduped: string[] = [];

  for (const timeZone of orderedTimeZones) {
    const normalizedTimeZone = timeZone?.trim();
    if (!normalizedTimeZone || seen.has(normalizedTimeZone)) {
      continue;
    }

    seen.add(normalizedTimeZone);
    deduped.push(normalizedTimeZone);
  }

  return deduped;
}

export function isKnownIanaTimeZone(timeZone: string) {
  const normalized = timeZone?.trim();
  if (!normalized) {
    return false;
  }

  try {
    // Throws RangeError when timezone is not recognized by the runtime.
    Intl.DateTimeFormat(undefined, { timeZone: normalized });
    return true;
  } catch (_error) {
    // Intl may not include full tzdata in some runtimes; fall back to moment-timezone.
    return !!moment.tz.zone(normalized);
  }
}
